import { z } from 'zod';
import { baseListQuery, dateOnlySchema, uuidSchema } from './common';
import type { FileDto } from './files';
import {
  dateKeysBetween,
  monthEndKey,
  shiftDateKey,
  weekStartKey,
  WAYBILL_CORRECTION_DAYS,
} from './time';
import { formatNameWithInitials } from './person-name';
import type { VehicleOwnership } from './vehicles';
import type { RequestStatus, VehicleRequestType } from './enums';

// ── Путевой лист (ADR 0037) ──
// Лист — документ строгой отчётности: серия, номер, журнал учёта. Черновика у него нет: он
// выписывается с маршрута сразу выданным (ADR 0050), а испорченный бланк аннулируют с причиной.

export const WAYBILL_STATUSES = ['issued', 'cancelled'] as const;
export const waybillStatusSchema = z.enum(WAYBILL_STATUSES);
export type WaybillStatus = (typeof WAYBILL_STATUSES)[number];

export const waybillStatusLabels: Record<WaybillStatus, string> = {
  issued: 'Выдан',
  cancelled: 'Аннулирован',
};

export const waybillStatusColors: Record<WaybillStatus, string> = {
  issued: 'green',
  cancelled: 'red',
};

/**
 * Бланки. Расширение области идёт значением в справочнике, а не второй схемой (ADR 0037).
 */
export const WAYBILL_FORM_CODES = ['4p', 'leg3', 'esm2'] as const;
export const waybillFormCodeSchema = z.enum(WAYBILL_FORM_CODES);
export type WaybillFormCode = (typeof WAYBILL_FORM_CODES)[number];

/**
 * Бланки, которые закрепляются за типом ТС в справочнике (ADR 0065).
 *
 * ЭСМ-2 сюда не входит, и это не забывчивость: он выписывается порталом **по виду заявки**
 * (`esm2Mode`), а не по типу техники — заявку на технику берут в работу, и листы на все недели
 * её срока рождаются сами. Проставленный типу `'esm2'` не включил бы ничего, зато у машины,
 * которой этот тип стоит, лист на рейс печатался бы недельным бланком строительной машины.
 *
 * Умолчание — 4-П: у собственной техники лист есть всегда, а форма № 3 включается признаком
 * «легковой транспорт» в справочнике типов.
 */
export const TYPE_WAYBILL_FORM_CODES = ['4p', 'leg3'] as const;
export const typeWaybillFormCodeSchema = z.enum(TYPE_WAYBILL_FORM_CODES, {
  error: 'Типу закрепляют бланк 4-П или форму № 3: ЭСМ-2 портал выписывает сам по заявке',
});
export type TypeWaybillFormCode = (typeof TYPE_WAYBILL_FORM_CODES)[number];

/**
 * Whether a stored code is a form a type may carry. The Drizzle column is typed narrow, but until
 * `drizzle/pending/vehicle-type-waybill-form-check.sql` narrows the CHECK the database still admits
 * 'esm2' on a type (ADR 0128), so a reader whose answer would change on such a row asks this
 * instead of trusting the type.
 */
export function isTypeWaybillFormCode(code: string): code is TypeWaybillFormCode {
  return (TYPE_WAYBILL_FORM_CODES as readonly string[]).includes(code);
}

/** Бланк типа по умолчанию: им заводится тип, у которого не сказано обратного (миграция 0094). */
export const DEFAULT_TYPE_WAYBILL_FORM: TypeWaybillFormCode = '4p';

/**
 * Легковой ли это транспорт — так вопрос стоит в справочнике типов, и бланк из него следует.
 * Признак и код формы — одно и то же знание в двух видах, поэтому колонки «легковой» нет:
 * второй источник правды разошёлся бы с первым на первой же правке.
 */
export function isPassengerTypeForm(formCode: WaybillFormCode): boolean {
  return formCode === 'leg3';
}

/** Обратный перевод: чекбокс формы справочника — в значение, которое ляжет в справочник. */
export function typeWaybillFormOf(isPassenger: boolean): TypeWaybillFormCode {
  return isPassenger ? 'leg3' : DEFAULT_TYPE_WAYBILL_FORM;
}

export const waybillFormLabels: Record<WaybillFormCode, string> = {
  '4p': 'Форма 4-П (грузовой автомобиль)',
  leg3: 'Форма № 3 (легковой автомобиль)',
  esm2: 'Форма ЭСМ-2 (строительная машина)',
};

/**
 * Как форму называют в столбце журнала: полная подпись туда не влезает, а на вопрос «какой это
 * бланк» отвечает и короткая. Полная остаётся в подсказке фильтра — там место есть.
 */
export const waybillFormShortLabels: Record<WaybillFormCode, string> = {
  '4p': '4-П',
  leg3: '№ 3',
  esm2: 'ЭСМ-2',
};

// ── На какой рейс выписывается лист (ADR 0037 п. 1, ADR 0041) ──

export interface WaybillRequirementInput {
  /** Only a freight request has a route sheet. */
  requestType: VehicleRequestType;
  /** A rental vehicle's sheet belongs to its lessor. */
  ownership: VehicleOwnership;
  /** Stored vehicle type form; old databases may still hold 'esm2' until the pending CHECK. */
  formCode: WaybillFormCode;
}

export interface WaybillRequirement {
  /** Issuable route form; `null` means the route cannot issue a sheet. */
  formCode: WaybillFormCode | null;
  /**
   * Shown when issuance is blocked. A null reason with null form means the request has no route
   * sheet by its kind, so no issue control or explanation is shown (ADR 0041).
   */
  reason: string | null;
}

/** Единственная причина, по которой у рейса не бывает листа при живой заявке-грузоперевозке. */
export const RENTAL_WAYBILL_REASON = 'Путевой лист на арендную технику выписывает арендодатель';

/**
 * A legacy type may still store 'esm2' until the pending CHECK ships (ADR 0128). The owner must
 * choose its route form; silently turning it into 4-P would issue the wrong controlled document.
 */
export const UNSETTLED_TYPE_WAYBILL_REASON =
  'У типа ТС сохранён бланк ЭСМ-2, который нельзя выписать на рейс. Выберите для типа 4-П или форму № 3';

/**
 * Whether a freight request needs a route sheet (ADR 0041, ADR 0065). An on-site equipment
 * request has no route sheet; a rental vehicle's sheet belongs to its lessor.
 *
 * An own vehicle normally carries 4-P or form No. 3. Until the pending CHECK ships, an old type
 * may still store 'esm2' (ADR 0128); a freight request must not advertise a weekly sheet as its
 * route form or silently choose another form for the owner.
 */
export function waybillRequirement(input: WaybillRequirementInput): WaybillRequirement {
  if (input.requestType !== 'freight_transport') return { formCode: null, reason: null };
  if (input.ownership !== 'own') return { formCode: null, reason: RENTAL_WAYBILL_REASON };
  if (!isTypeWaybillFormCode(input.formCode)) {
    return { formCode: null, reason: UNSETTLED_TYPE_WAYBILL_REASON };
  }
  return { formCode: input.formCode, reason: null };
}

// ── ЭСМ-2: лист на неделю работы машины на площадке ──

/**
 * Как заявка получает недельные листы (ADR 0100 §5). Ответов три, а не два:
 *
 * - `none` — листов у заявки быть не должно вовсе: не заказ техники на объект, не в работе,
 *   архивная или арендная. Действующие аннулируются.
 * - `auto` — портал ведёт бумагу сам: заявку взяли в работу, и на каждую неделю срока рождается
 *   свой лист (ADR 0060). Так работает вся нелинейная спецтехника.
 * - `on_demand` — линейный заказ: новых недель портал не заводит, но уже выписанные ведёт.
 *
 * Третий режим появился не ради послабления. Линейная машина вечером возвращается на базу и за
 * день успевает поработать на нескольких площадках (ADR 0100): недели стояния на объекте у неё
 * нет, и выписанный наперёд недельный лист утверждал бы работу, которой не было. Кому такой лист
 * всё-таки нужен — решает человек, неделя за неделей.
 */
export const ESM2_MODES = ['none', 'auto', 'on_demand'] as const;
export type Esm2Mode = (typeof ESM2_MODES)[number];

/**
 * В каком режиме ведутся листы ЭСМ-2 этой заявки.
 *
 * Третий вход в то же семейство, что `waybillRequirement` (лист на рейс грузоперевозки) и
 * `routeWaybillForm` (лист на рейс маршрута), и правило здесь своё: бланк выбирается **путём
 * выписки**, а не справочником типов. Проставить `vehicle_types.waybill_form_code='esm2'` типам
 * спецтехники нельзя — заполненный бланк у типа `assertRouteVehicle` считает разрешением заводить
 * грузовой рейс (ADR 0057 Р13).
 *
 * Статус спрашивается наравне с видом заявки: у заявки, ушедшей из работы, действующих листов быть
 * не должно, и «сколько их нужно» — это ноль, а не «столько же, сколько было».
 *
 * Линейность спрашивается последней, и только у тех, кому листы вообще положены: у арендной
 * машины и у отменённой заявки ответ один — `none`, — и признак типа ничего в нём не меняет
 * (ADR 0100: «линейная арендная техника ничего не меняет»).
 */
export function esm2Mode(input: {
  requestType: VehicleRequestType;
  status: RequestStatus;
  ownership: VehicleOwnership | null;
  deletedAt?: string | null;
  /**
   * Линейный ли **заказанный** тип (`vehicle_types.is_linear`, миграция 0127). Именно заказанный,
   * а не тип назначенной машины: заказ решает, как заявка ведётся, ещё до того, как под неё нашли
   * единицу (ADR 0100 §1). Снимка признака в заявке нет — он читается живым из справочника.
   */
  isLinear: boolean;
}): Esm2Mode {
  if (input.requestType !== 'special_equipment') return 'none';
  if (input.status !== 'confirmed' && input.status !== 'done') return 'none';
  if (input.deletedAt) return 'none';
  // Лист на арендную технику выписывает арендодатель — тем же правилом, что и у грузоперевозки.
  if (input.ownership !== 'own') return 'none';
  return input.isLinear ? 'on_demand' : 'auto';
}

/** Неделя работы машины: то, что печатается одним бланком ЭСМ-2. */
export interface Esm2Period {
  /** Первый рабочий день недели — он же дата листа и «Период работы: с». */
  from: string;
  /** Последний рабочий день недели — «Период работы: по». */
  to: string;
}

/**
 * Пересекаются ли два периода хоть одним днём.
 *
 * Общий ответ на вопрос «про одни ли это дни», которым после месячного разреза (ADR 0142)
 * адресуется бумага: неделя перестала быть ключом листа — в ней законно живут два документа, — а
 * дни остались тем, что у листа и у ожидания сравнимо всегда.
 *
 * Границы включительные: лист «31–31 августа» и период «31.08–06.09» — про один и тот же день.
 */
export function periodsOverlap(a: Esm2Period, b: Esm2Period): boolean {
  return a.from <= b.to && b.from <= a.to;
}

/**
 * Срок заявки, разрезанный на периоды листов: по бланку на каждый.
 *
 * Границ, режущих срок, три: его начало, его конец и **две календарные** — воскресенье и последнее
 * число месяца.
 *
 * Неделя режет потому, что семь строк «пн…вс» впечатаны в бланк и лист не умеет пересечь
 * воскресенье. Месяц режет потому, что бланк ведут месяцем: недельные итоги сводят в месячные, и
 * документ, у которого работа лежит в двух отчётных периодах, не закрывает ни одного (ADR 0142).
 * Прежде месяц не резал, а печатался в графе «месяца» парой номеров («08–09»); решение отменено
 * заказчиком: у недели 31.08–06.09 должно быть два листа — «31–31 августа» и «1–6 сентября».
 *
 * Два разреза в одном цикле, а не один поверх другого: у обеих границ роль одна — оборвать
 * текущий период раньше конца срока, — и ближайшая из них и есть конец листа. Второй проход по
 * готовым неделям дал бы тот же ответ дороже и завёл бы второе место, где написано, чем режется
 * бумага.
 *
 * Пустая дата окончания — однодневный срок: так её читают и отбор среза, и подписи присутствия.
 */
export function esm2Periods(dateFrom: string, dateTo: string | null): Esm2Period[] {
  const last = dateTo || dateFrom;
  if (!dateFrom || last < dateFrom) return [];
  const periods: Esm2Period[] = [];
  let from = dateFrom;
  while (from <= last) {
    const weekEnd = shiftDateKey(weekStartKey(from), 6);
    const monthEnd = monthEndKey(from);
    const bound = monthEnd < weekEnd ? monthEnd : weekEnd;
    const to = bound < last ? bound : last;
    periods.push({ from, to });
    from = shiftDateKey(to, 1);
  }
  return periods;
}

/**
 * Семь дней недели листа — от понедельника до воскресенья, независимо от того, какой кусок недели
 * покрывает сам лист: строки «пн…вс» впечатаны в бланк, и сдвигать их нельзя.
 *
 * `inPeriod` отвечает, попал ли день в период листа, и на бумагу этот ответ снова идёт (ADR 0142):
 * число печатается только у своего дня, а строка чужого остаётся пустой. Прежде печатались все
 * семь чисел — графа объекта из строк ушла, и пустая строка перестала значить «портал не знает,
 * работала ли машина в субботу», — но с месячным разрезом два листа одной недели получили бы
 * одинаковую сетку чисел, и часы 1 сентября вписали бы в августовский бланк.
 */
export function esm2WeekDays(period: Esm2Period): { date: string; inPeriod: boolean }[] {
  const monday = weekStartKey(period.from);
  return dateKeysBetween(monday, shiftDateKey(monday, 6)).map((date) => ({
    date,
    inPeriod: date >= period.from && date <= period.to,
  }));
}

/** Действующий лист ЭСМ-2 заявки — то, с чем сверяется нужный набор недель. */
export interface Esm2Sheet {
  id: string;
  periodFrom: string;
  periodTo: string;
  vehicleId: string;
  driverPersonId: string;
}

/**
 * Недели, которые человек уже попросил, — подрезанные сроком заявки (ADR 0100 §5).
 *
 * Это второй способ ответить на вопрос «какие недели должны быть выписаны», и нужен он режиму
 * `on_demand`. В `auto` набор берётся из срока (`esm2Periods`): портал сам решает, сколько бумаги
 * нужно заявке. У линейного заказа такого решения нет вовсе — недели называет человек, — и
 * единственный след его просьбы это сами выписанные листы.
 *
 * Отсюда правило подрезки, и оно ровно то же, каким срок правит бумагу в `auto`:
 *
 * - неделя, целиком выпавшая из срока (заявку сократили или сдвинули её начало), в ответ не
 *   входит: её лист аннулируется, а замены не выписывается — работы в эти дни не будет;
 * - неделя, пересечённая сроком, возвращается **подрезанной**: лист доводится до нового последнего
 *   дня. Прежде это стоило номера — лист аннулировали и выписывали заново, — а с правилом `trim`
 *   (Р6 плана `docs/vehicle-request-actual-end-date-plan.md`) сокращение делается на месте: состав
 *   и начало те же, портал лишь отнимает дни, которых работа не знала. Правка не сработает там,
 *   где подрезанный кусок режется дальше месячной границей (см. ниже): хвост тогда ждёт второй
 *   документ, и лист по-прежнему переоформляется парой — это пятое условие Р6.
 *
 * Расширять неделю подрезка не умеет намеренно: продлённый срок новых листов линейному заказу не
 * приносит (их не заводит и сам режим), а раздвигать границы уже выданного бланка значило бы жечь
 * номер ради дней, которых человек не просил.
 */
export function esm2RequestedPeriods(
  existing: readonly Esm2Sheet[],
  dateFrom: string,
  dateTo: string | null,
): Esm2Period[] {
  // Пустая дата окончания — однодневный срок: так её читают и `esm2Periods`, и отбор среза.
  const last = dateTo || dateFrom;
  if (!dateFrom || last < dateFrom) return [];
  // Ключом — сами границы: неделю закрывают и две машины сразу (ADR 0100 §7), и подрезка даёт им
  // один и тот же отрезок. Дважды выписывать его не надо — в ответе это одна неделя.
  const byBounds = new Map<string, Esm2Period>();
  for (const sheet of existing) {
    const from = sheet.periodFrom < dateFrom ? dateFrom : sheet.periodFrom;
    const to = sheet.periodTo > last ? last : sheet.periodTo;
    if (to < from) continue;
    /*
     * Подрезанный кусок режется дальше теми же границами, что и срок в `auto` (ADR 0142): лист,
     * выписанный до месячного разреза, при первой же правке срока переоформляется парой, а не
     * воспроизводит сам себя переходным периодом. Недельная граница здесь не срабатывает никогда —
     * лист внутри своей недели по построению, — и стоит тут не ради неё, а ради единственного
     * места, где написано, чем режется бумага.
     */
    for (const period of esm2Periods(from, to)) {
      byBounds.set(`${period.from}|${period.to}`, period);
    }
  }
  return [...byBounds.values()].sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : 0));
}

/**
 * Сокращение периода уже выданного листа: тот же номер, тот же состав, новый последний день (Р6
 * плана `docs/vehicle-request-actual-end-date-plan.md`).
 *
 * Прежнего конца здесь нет намеренно: он лежит в самом листе, а план говорит только о том, что
 * меняет. Ради «было → стало» лист всё равно читают — и исполнитель, чтобы записать
 * `period_to_original`, и предпросмотр, чтобы показать человеку обе даты, — а второй источник той
 * же даты разошёлся бы с первым на первой же гонке.
 */
export interface Esm2Trim {
  /** Лист, который правится. Номер его не меняется и в расход не идёт. */
  waybillId: string;
  /**
   * Новый последний день периода: строго раньше прежнего и не раньше первого дня листа. Обе
   * границы держит сама сверка (Р6) — расширять выданный бланк она не умеет ни при каких входных
   * данных, включая возврат срока к прежнему значению.
   */
  to: string;
}

/** Что сделать с бумагой, чтобы она сошлась с заявкой. */
export interface Esm2SyncPlan {
  /** Листы под аннулирование: их данные разошлись с заявкой либо недели больше нет. */
  cancel: string[];
  /** Недели, на которые листа не осталось: их выписывают новыми номерами. */
  issue: Esm2Period[];
  /**
   * Листы, которым хватает правки периода: третий исход сверки рядом с «аннулировать» и
   * «выписать» (Р5, Р6). Состав тот же, начало то же, конец ближе, а отнятые дни не нужны
   * никакому другому ожиданию. Номер не расходуется, замены не выписывается, графа замен у такого
   * листа пуста — документ остаётся собой и лишь теряет дни с конца.
   */
  trim: Esm2Trim[];
}

/**
 * Сверка «что должно быть» с «что есть» — сердце ведения ЭСМ-2 порталом.
 *
 * Выданный лист не правится никогда: бланк строгой отчётности с исправленными задним числом
 * графами — не документ. Изменить содержание можно только аннулированием номера и выпиской
 * нового. И столь же твёрдо обратное: сошлось — не трогаем. Пустые списки в ответе означают, что
 * бумага в порядке, и ни один номер не сгорит.
 *
 * Исключение из первого правила ровно одно, и названо оно поимённо (Р5, Р6 плана
 * `docs/vehicle-request-actual-end-date-plan.md`): **сокращение периода**. Нового содержания у
 * него нет — машина, человек и первый день те же, — портал лишь отнимает дни с конца, которых
 * работа не знала: заказ закрыли фактической датой, срок сократили, неделя ушла за границу. Жечь
 * ради этого номер незачем, и лист правится на месте (`trim`).
 *
 * Правка умеет **только отнимать дни**. Расширения нет ни при каких входных данных, включая
 * возврат срока к прежнему значению: раздвинуть напечатанное «Период работы: с 03.08 по 05.08»
 * нечем, кроме нового номера. Отнимать при этом можно и то, что уже напечатано, — распечатанный
 * экземпляр остаётся с прежней графой периода, и расхождение это намеренное (Р13): бумага
 * отвечает «до какого числа разрешали работать», портал — «сколько отработали».
 *
 * И отнимать можно только **в пустоту**: если отнятые дни нужны другой неделе набора, лист
 * по-прежнему аннулируется и выписывается заново. Это пятое условие правила, и держится оно на
 * той же печатной графе. У закрытия фактической датой хвост не нужен никому: работы в эти дни не
 * было, бумаги на них не будет. У недели, разрезанной надвое, хвост забирает соседний документ —
 * и после правки на площадке лежали бы два действующих бланка, у обоих в графе «Период работы»
 * стояла бы среда, и один и тот же день можно было бы вписать дважды. Аннулирование этого не
 * допускает: старый бланк уходит из оборота целиком.
 *
 * Отработанные недели неприкосновенны. Лист, чей последний день уже прошёл (`canCancelWaybill`),
 * не аннулируется и не считается расхождением: машина эти дни отстояла, заказчик заполнил оборот.
 * Больше того — такой лист закрывает свою календарную неделю целиком: выписать на неё второй,
 * «правильный», значило бы выдать два документа на одну работу. Прошедшая неделя, листа не
 * имевшая, теперь тоже не выписывается сама (ADR 0101, Р21).
 *
 * Обе неприкосновенности снимаются коррекцией, и разными ключами: `unlockWaybillIds` называет
 * листы, которые правят, `correction` разрешает выписать прошедшую неделю. Оба приходят от
 * сервера, уже спросившего право и причину.
 *
 * Режим (ADR 0100 §5) меняет в этой работе ровно две вещи — набор недель приходит уже посчитанным
 * (`wanted`), а здесь остаются сверка машиниста и запрет заводить новые недели в `on_demand`.
 * Всё прочее у режимов общее: выданный лист не правится, отработанная неделя не трогается.
 */
export function esm2SyncPlan(input: {
  /** Режим ведения листов (`esm2Mode`): им отличается линейный заказ от обычного. */
  mode: Esm2Mode;
  /**
   * Недели, которые должны быть выписаны; пусто — листов быть не должно. Считает вызывающий:
   * в `auto` это срок заявки (`esm2Periods`), в `on_demand` — уже выписанное, подрезанное сроком
   * (`esm2RequestedPeriods`).
   */
  wanted: readonly Esm2Period[];
  /** Действующие листы заявки. */
  existing: readonly Esm2Sheet[];
  /** Машина, какой она стоит на заявке сейчас. */
  vehicleId: string | null;
  /**
   * Машинист, которого этим действием назвали. `null` в `on_demand` — не называли: см. сверку
   * человека ниже.
   */
  driverPersonId: string | null;
  /** Сегодня по МСК: им отделяются отработанные недели от предстоящих. */
  today: string;
  /**
   * Листы, которые коррекция назвала поимённо, — им отработанная неделя не защита (Р11, ADR 0101).
   *
   * Идентификаторами, а не понедельниками: после линейной техники действующий лист уникален по
   * `(sourceRequestId, periodFrom, vehicleId)` (ADR 0100 п. 7), то есть в одной неделе у заявки
   * законно живут листы двух машин. Понедельник как ключ разблокировал бы не тот лист или сразу
   * оба — и сжёг бы номер машины, к которой коррекция отношения не имеет.
   *
   * Список — перечень намерения, а не авторизация: право (`waybills.correct`), принадлежность
   * листа заявке и границу Р29 сервер проверяет отдельно и всегда.
   *
   * Разблокировка адресная и в другую сторону не растёт: если в той же неделе остался лист, о
   * котором не просили, неделя для выписки закрыта — названный лист аннулируется, а замены ему не
   * будет. Поэтому вызывающий перечисляет все листы недели, которую собирается переоформить, а не
   * первый попавшийся.
   */
  unlockWaybillIds?: readonly string[];
  /**
   * Контекст проверенной операции коррекции (Р21): право спрошено, причина есть, эффективная дата
   * в границе Р29. Только с ним сверка выписывает неделю, которая уже кончилась.
   *
   * Без него прошедшая неделя не выписывается вовсе — и это закрытая дыра, а не новая строгость:
   * сегодня неделя, добавленная сдвигом `dateFrom` назад, выписывается сверкой молча — без права,
   * причины и следа (§1, дыра 3). Признак приходит от сервера, который право уже спросил, а не из
   * тела запроса: тело авторизацией не является.
   */
  correction?: { allowed: true };
}): Esm2SyncPlan {
  const unlocked = new Set(input.unlockWaybillIds ?? []);
  const correcting = input.correction?.allowed === true;
  /**
   * Дни неприкосновенных листов — отрезками, а не понедельниками их недель (ADR 0142).
   *
   * Замок отвечает на вопрос «не выпишем ли мы второй документ на уже сделанную работу», и работа
   * лежит в днях листа, а не в его календарной неделе. Пока лист был всегда недельным, разницы не
   * было; с месячным разрезом она появляется в обе стороны: отработанный кусок «31–31 августа» не
   * должен запирать сентябрьские дни той же недели, а отработанный лист на всю неделю обязан
   * запирать оба куска сразу.
   */
  const locked: Esm2Period[] = [];
  const kept = new Set<string>();
  /** Дни, на которые лист уже выписывался: ими ограничен `on_demand` — тем же отрезком, что и замок. */
  const requested: Esm2Period[] = [];
  const cancel: string[] = [];
  const trim: Esm2Trim[] = [];
  /**
   * Листы, не совпавшие с набором точно, — вместе с уже посчитанным ответом о составе.
   *
   * Проходов поэтому два, и второй не оптимизация, а условие правильности: сокращать лист можно
   * только в ту неделю, за которой ещё не стоит документа, а «не стоит» знает лишь **весь** первый
   * проход. Реши мы это одним циклом — лист пн–вс, встреченный раньше своего точного двойника
   * пн–вт, сократился бы в занятую неделю, и у заявки оказалось бы два действующих документа на
   * одни и те же дни.
   */
  const unmatched: { sheet: Esm2Sheet; sameComposition: boolean }[] = [];

  for (const sheet of input.existing) {
    requested.push({ from: sheet.periodFrom, to: sheet.periodTo });
    // У ЭСМ-2 дата листа — первый рабочий день недели, а граница аннулирования — последний.
    // Названный коррекцией лист эту границу проходит: его прошедшая неделя и есть предмет правки.
    if (
      !unlocked.has(sheet.id) &&
      !canCancelWaybill({ issuedForDate: sheet.periodFrom, periodTo: sheet.periodTo }, input.today)
    ) {
      locked.push({ from: sheet.periodFrom, to: sheet.periodTo });
      continue;
    }
    /*
     * Машинист сверяется не всегда, и это не поблажка, а разная природа графы.
     *
     * В `auto` человек один на всю заявку: его называют переводом в работу, и все недели срока
     * выписываются на него — разошёлся с заявкой, значит лист переписывается.
     *
     * В `on_demand` машиниста называют на каждую неделю отдельно (ADR 0100 §6: графа в бланке
     * одна на неделю, а водитель у линейной машины каждый день свой), и «машиниста заявки» не
     * существует вовсе. Сверять там не с чем, поэтому человек сверяется только тогда, когда его
     * назвали этим же действием: свести две недели к одному имени молча значило бы напечатать в
     * листе того, кто ту неделю не работал (ADR 0083 — портал не подставляет людей молча).
     */
    const sameDriver =
      input.mode === 'on_demand' && input.driverPersonId === null
        ? true
        : sheet.driverPersonId === input.driverPersonId;
    /*
     * Машина сверяется по той же границе, что и человек, и по той же причине.
     *
     * В `auto` машина у заявки одна: её называют переводом в работу, все недели срока выписаны на
     * неё, и разошедшийся лист переписывается — в бланке напечатан госномер, исправить его нельзя.
     *
     * В `on_demand` «машины заявки» для листа не существует: неделю на объекте закрывают две
     * разные единицы, и какая из них стоит в этом бланке, решил человек при выписке (ADR 0100
     * §7). Сверять там не с чем — сравнение с назначением сожгло бы номер, выписанный на вторую
     * машину, и перепечатало бы его на первую, то есть сделало бы ровно то, ради отмены чего
     * заведён режим. Машина заявки остаётся при этом машиной по умолчанию (ADR 0100 §4) — но это
     * про планирование дней, а не про уже выданную бумагу.
     */
    const sameVehicle = input.mode === 'on_demand' ? true : sheet.vehicleId === input.vehicleId;
    /*
     * Состав листа — то, что решает «оставить» против «аннулировать», и ровно им же Р6 отбирает
     * сокращаемые: у правки периода состав обязан совпасть теми же двумя сверками, что и у
     * совпадения. Своей, более мягкой, сверки у сокращения нет намеренно — иначе портал правил бы
     * лист, в котором напечатан другой человек или другой госномер, то есть чинил бы правкой
     * ровно то, ради чего заведено аннулирование.
     */
    const sameComposition = sameVehicle && sameDriver;
    const matches =
      sameComposition &&
      input.wanted.some((p) => p.from === sheet.periodFrom && p.to === sheet.periodTo);
    if (matches) kept.add(sheet.periodFrom);
    else unmatched.push({ sheet, sameComposition });
  }

  /*
   * Второй проход: сокращать или жечь (Р6).
   *
   * Лист попадает в `trim` при всех условиях сразу — состав совпал, первый день не двигается,
   * новый последний день не раньше первого и строго раньше прежнего, а отнятые дни не нужны
   * никакой другой неделе набора. Всё прочее остаётся парой «аннулировать плюс выписать»:
   * сменилась машина или человек, сдвинулось начало, период расширяется. Внутри своей календарной
   * недели правка остаётся по построению — начало то же, а конец только уменьшается, — и
   * ограничение базы «лист не пересекает воскресенье» она выдержит.
   *
   * Пятое условие и отделяет закрытие от разреза. Заказ, закрытый фактической датой, хвоста
   * никому не отдаёт: работы в эти дни не было, и бумаги на них не будет. А неделю, разрезанную
   * надвое (месячной границей или сменой состава у соседнего планировщика), правка оставила бы с
   * двумя действующими бланками: печатный экземпляр сохраняет прежний, невырезанный период (Р13),
   * оба показывали бы среду, и один день можно было бы вписать дважды. Аннулирование этого не
   * допускает — старый бланк уходит из оборота целиком.
   *
   * Неделя, отданная сокращению, помечается тем же `kept`, каким помечается совпавшая: для отбора
   * `issue` ниже она закрыта — за ней стоит документ, и второй выписывать не на что.
   */
  for (const { sheet, sameComposition } of unmatched) {
    const target =
      sameComposition && !kept.has(sheet.periodFrom)
        ? input.wanted.find(
            (p) => p.from === sheet.periodFrom && p.to >= p.from && p.to < sheet.periodTo,
          )
        : undefined;
    if (target) {
      // Хвост правки — дни за новым концом и по прежний. Ждёт их хоть одна неделя набора — правка
      // отменяется целиком, и лист идёт обычным порядком: аннулирование плюс выписка.
      const tail = { from: shiftDateKey(target.to, 1), to: sheet.periodTo };
      if (!input.wanted.some((p) => periodsOverlap(p, tail))) {
        trim.push({ waybillId: sheet.id, to: target.to });
        kept.add(sheet.periodFrom);
        continue;
      }
    }
    cancel.push(sheet.id);
  }

  const issue = input.wanted.filter(
    (p) =>
      !kept.has(p.from) &&
      !locked.some((sheet) => periodsOverlap(p, sheet)) &&
      /*
       * Кончившийся период выписывается только проверенной операцией (Р21). Граница считается по
       * его концу, а не по понедельнику его недели: тем же концом её считает `canCancelWaybill`, и
       * второй расчёт того же «когда работа кончилась» разошёлся бы с первым на шесть дней.
       *
       * Проверка стоит рядом с `locked`, а не вместо него: `locked` защищает **выписанную**
       * отработанную неделю от второго документа на ту же работу, а это правило — прошедшую
       * неделю, листа не имевшую вовсе. Случаи разные, и снимаются они разными ключами:
       * `unlockWaybillIds` и `correction`.
       */
      (correcting || p.to >= input.today) &&
      // Своих недель `on_demand` не заводит: выписка — действие человека, и сверке остаётся лишь
      // переоформлять то, что он уже попросил. Правило стоит здесь, а не только в расчёте
      // `wanted`, потому что оно и есть определение режима: ошибись вызывающий набором — портал
      // всё равно не выпишет линейному заказу неделю, которой у него не было.
      (input.mode !== 'on_demand' || requested.some((sheet) => periodsOverlap(p, sheet))),
  );
  return { cancel, issue, trim };
}

/** Номер бланка с ведущими нулями: «00000004897» при ширине 8 и больше. */
export function formatWaybillNumber(num: number, width: number): string {
  return String(num).padStart(width, '0');
}

/**
 * Как лист называют в журнале и в разговоре: «260604-646-00000004897». Без серии остаётся один
 * номер — префикс печатается в своей графе бланка и у новых серий пуст, пока его не задали.
 */
export function waybillDisplayNumber(prefix: string, num: number, width: number): string {
  return `${prefix}${formatWaybillNumber(num, width)}`;
}

/**
 * Дата на бланке: «23.08.2026» из календарного ключа «2026-08-23».
 *
 * Бумага пишет день по-русски, а ISO-запись читается в графе «Дата» заводским штампом, а не днём
 * выезда. Вид заводится один на оба бланка и на обе даты, которые туда идут, — дату листа и выдачу
 * удостоверения: второй расчёт того же формата разошёлся бы с первым на первой же правке.
 *
 * Пустая дата остаётся пустой: графу допишут от руки (ADR 0064), а «..» портал не печатает. Не
 * похожее на ключ даты возвращается как есть — снимок печатается как выдан (ADR 0037 п. 10), и
 * дату, уже переложенную при выписке, функция обязана оставить в покое.
 */
export function formatWaybillDate(iso: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const [yyyy, mm, dd] = iso.split('-') as [string, string, string];
  return `${dd}.${mm}.${yyyy}`;
}

/**
 * The trailer row of the 4-P tear-off coupons: makes on one line, plates on the other
 * («ШМИТЦ SPR-24, КРОНА SDP27» and «ВХ933277, ЕН806277»), in slot order.
 *
 * A pair with a missing half keeps its place with a dash while the other trailer fills that line:
 * otherwise the second trailer's plate would print under the first one's make, and the coupon would
 * pair them wrongly. A line no trailer fills stays empty and is written by hand, like an empty box
 * of the header (ADR 0064).
 */
export function trailerCouponLines(
  pairs: readonly { model?: string | null; regNumber?: string | null }[],
): { brands: string; regNumbers: string } {
  const filled = pairs
    .map((p) => ({ model: (p.model ?? '').trim(), regNumber: (p.regNumber ?? '').trim() }))
    .filter((p) => p.model || p.regNumber);
  const line = (values: string[]): string =>
    values.some(Boolean) ? values.map((v) => v || '—').join(', ') : '';
  return {
    brands: line(filled.map((p) => p.model)),
    regNumbers: line(filled.map((p) => p.regNumber)),
  };
}

/**
 * Ключи снимка — они же плейсхолдеры бланка. Объявлены списком, а не выводятся из кода сборки:
 * по нему тест сверяет шаблон, и графа, которой в снимке нет, не доедет до бумаги молча
 * (ADR 0037 п. 10).
 */
export const WAYBILL_SNAPSHOT_KEYS = [
  'org_name',
  'org_address',
  'org_phone',
  'org_okpo',
  'org_ogrn',
  'waybill_series',
  'waybill_number',
  'waybill_date',
  'vehicle_brand',
  'vehicle_reg_number',
  'vehicle_garage_number',
  'vehicle_inventory_number',
  'trailer1_brand',
  'trailer1_reg_number',
  'trailer2_brand',
  'trailer2_reg_number',
  /*
   * The trailers in the tear-off coupons of the 4-P form (ADR 0138): one line of makes and one of
   * plates, because a coupon has a single "Прицепы ___ Государственный номерной знак ___" row
   * where the header has a pair of boxes per trailer. Assembled by the server
   * (`trailerCouponLines`), not glued from the two pairs in the template: a cell joining two
   * placeholders would print an orphaned comma under a single trailer.
   */
  'trailers_brands',
  'trailers_reg_numbers',
  'driver_fio',
  /*
   * «Дегтярь И.В.» — тем видом расшифровку подписи пишут от руки, и графа под неё узкая: полное
   * ФИО в неё не встаёт. Отдельным ключом, а не сокращением при печати: снимок обязан помнить не
   * только человека, но и то, как его имя напечатано на выданной бумаге.
   */
  'driver_short_name',
  'driver_snils',
  'driver_personnel_no',
  'driver_license_number',
  'driver_license_issued_on',
  'communication_kind',
  'transportation_kind',
  /*
   * Заказчик листа — «в чьё распоряжение» у 4-П и «Заказчик» у ЭСМ-2: наименование, адрес офиса и
   * телефон генподрядчика из настройки портала (`waybill_customer`, миграция 0164). Настройка одна
   * на все бланки: пока генподрядчик один, у вопроса «в чьё распоряжение выписан лист» ответ один,
   * и брать его у заявки значило бы называть заказчиком того, кто технику попросил.
   *
   * Смысл этих трёх ключей сменился вместе с настройкой, и старые снимки хранят прежнее — **объект
   * заявки**: его наименование, его адрес и телефон ответственного за встречу машины. Повторная
   * печать выданного листа даёт то, что было выдано (ADR 0037 п. 10), поэтому один и тот же ключ в
   * листах до и после этой работы значит разное; на какой объект выписан лист, помнит `object_line`.
   */
  'customer_name',
  'customer_address',
  'customer_phone',
  /*
   * «Наименование и адрес объекта» одной строкой — тот объект, ради которого лист выписан.
   *
   * Один ключ, а не пара: склейку делает сервер, потому что в бланке под неё одна ячейка, и пустой
   * адрес в паре напечатал бы осиротевшую запятую. Заполняют его оба сборщика — у листа на рейс это
   * объект заявки шапки, — а печатает только ЭСМ-2: там объект стоит в шапке графы работ на обеих
   * сторонах бланка. В 4-П объект на бумагу не идёт вовсе (он живёт в задании водителю), но снимок
   * обязан помнить, на какой объект выписан лист.
   */
  'object_line',
  'task_from',
  'task_to',
  // Груз: количество первой строкой, комментарий заявки — второй (ADR 0071). Две строки, а не
  // две графы: в бланке на груз отведена одна ячейка, и разводит их перенос внутри неё.
  'task_cargo',
  // Графа «заказчик, телефон» 4-П: две строки — контакт погрузки и контакт разгрузки, по одному
  // на каждый конец маршрута (миграция 0062). Наименование заказчика туда не идёт: оно уже стоит
  // в шапке задания «в чьё распоряжение», а водителю в дороге нужен тот, кто откроет ворота.
  'task_contacts',
  // Время выезда графой 4-П. Клеток «ч» и «мин» формы № 3 здесь больше нет: задание в ней не
  // печатается вовсе (ADR 0071), и разбирать время по двум ключам стало не для чего.
  'task_departure_time',
  // Талоны 2–4 нижнего задания: в таблице бланка четыре строки, и рейс из четырёх заявок
  // печатается целиком. Пустые строки остаются пустыми — так же, как до маршрутов печаталась
  // единственная (ADR 0050 п. 15). Наименования заказчика у этих строк нет: графы под него в
  // 4-П не заведено, а печатала его только форма № 3 — до того, как задание ушло из неё вовсе
  // (ADR 0071).
  'task2_from',
  'task2_to',
  'task2_cargo',
  'task2_contacts',
  'task3_from',
  'task3_to',
  'task3_cargo',
  'task3_contacts',
  'task4_from',
  'task4_to',
  'task4_cargo',
  'task4_contacts',
  /*
   * Рейсы 5–7 (ADR 0068). В 4-П им отведены три нижние строки блока «Дополнительное задание
   * водителю» — три объединённые ячейки без единой графы внутри, поэтому туда идёт готовая строка
   * `*_line`: «откуда → куда, груз, контакты». Собирает её сервер, а не бланк: склейка
   * плейсхолдеров в ячейке напечатала бы у пустого рейса осиротевшие стрелку и запятые.
   *
   * Разобранных граф рядом больше нет: они размечались на обороте формы № 3, а задание в ней не
   * печатается вовсе (ADR 0071). Восьмой, девятой и десятой строк не осталось по той же причине —
   * в 4-П места под них нет, и печатать их стало негде.
   */
  'task5_line',
  'task6_line',
  'task7_line',
  // Графы диспетчера здесь нет намеренно: подпись и её расшифровку портал не печатает, и из
  // обоих бланков они стёрты. Документ подписывают на бумаге, а напечатанная рядом с пустой
  // линией фамилия читалась бы как подпись, которой нет. Кто выписал лист, помнят
  // `waybills.issued_by` и журнал аудита.

  // ── Графы ЭСМ-2 (лист на неделю работы машины) ──
  // «Дата составления» разложена на три клетки бланка: одной строкой «31.08.2026» их не заполнить.
  'waybill_date_dd',
  'waybill_date_mm',
  'waybill_date_yyyy',
  /** Код объекта затрат — им бухгалтерия разносит машино-часы по стройкам. */
  'object_code',
  // «Период работы: с __ по __ месяца __ года __» — фактические рабочие дни этой недели. Месяц
  // один на графу: у недели через границу месяца туда идут оба номера («08–09»).
  'period_from_day',
  'period_to_day',
  'period_month',
  'period_year',
  /*
   * Семь строк недели — числа месяца понедельника…воскресенья. Одни числа: графа «Наименование и
   * адрес объекта» в днях больше не печатается (`day1_object`…`day7_object` удалены), потому что
   * повторяла один и тот же адрес семь раз; теперь он стоит один раз в шапке графы (`object_line`).
   *
   * Следствие названо вслух: пустая графа дня перестала означать «портал не знает, работала ли
   * машина в этот день». Срок заявки ушёл с этих строк, и отвечает за него только «Период работы»
   * в шапке листа.
   */
  'day1_date',
  'day2_date',
  'day3_date',
  'day4_date',
  'day5_date',
  'day6_date',
  'day7_date',
] as const;

export type WaybillSnapshotKey = (typeof WAYBILL_SNAPSHOT_KEYS)[number];

/**
 * What the form boxes get when the sheet's snapshot predates them.
 *
 * A reprint must give the document handed out (ADR 0037 item 10), but it is printed on the
 * **current template**: the template file is read from disk on every request, the snapshot from
 * `waybills.data`. A box marked up after issue looks for a key the old snapshot lacks and prints
 * empty. The moves below fill such boxes, each only where an empty box would print less than the
 * sheet carries — something printed before and lost, or something the same button prints on the
 * neighbouring sheet — and never where emptiness lawfully means "the request had no such data".
 *
 * The sign is the absence of the key, not an empty value: on a new sheet an empty box means "no
 * data in the request", and nothing may be put in its place.
 *
 * Dates are never rewritten by these moves. A sheet issued before `formatWaybillDate` prints with
 * the ISO date, as issued: "a reprint gives the same document" outranks uniform papers across years.
 */
export function snapshotForPrint(data: Record<string, string>): Record<string, string> {
  if (
    'task_contacts' in data &&
    'object_line' in data &&
    'driver_short_name' in data &&
    'trailers_brands' in data
  ) {
    return data;
  }
  const patched = { ...data };
  /*
   * A sheet issued before the "customer, phone" box began to print the route contacts: the box
   * gets what it held at issue — the coupon customer's name.
   */
  if (!('task_contacts' in data)) {
    patched.task_contacts = data.customer_name ?? '';
    patched.task2_contacts = data.task2_customer ?? '';
    patched.task3_contacts = data.task3_customer ?? '';
    patched.task4_contacts = data.task4_customer ?? '';
  }
  /*
   * An ESM-2 sheet issued before the site moved from the seven day boxes into the header of the
   * work box. In the old snapshot it lies where the "Customer" box stood — `customer_name` and
   * `customer_address` — and is joined by the same rule the collectors use.
   *
   * Without this move a reprint of an old weekly sheet would come out **with no site at all**: the
   * day boxes are not marked up in the current template, and the header waits for a key the
   * snapshot lacks. That such a sheet prints the site twice — in the customer box and in the
   * header — is accepted on purpose: a repeat is better than a loss.
   */
  if (!('object_line' in data)) {
    patched.object_line = [data.customer_name, data.customer_address].filter(Boolean).join(', ');
  }
  /*
   * A sheet issued before the signature transcripts began to print the driver's name. The full
   * name is always in the snapshot and is shortened by the collectors' rule: otherwise a reprint
   * would leave the boxes empty, printing less than the same button prints on the neighbouring
   * sheet today.
   */
  if (!('driver_short_name' in data)) {
    patched.driver_short_name = formatNameWithInitials(data.driver_fio ?? '');
  }
  /*
   * A sheet issued before the tear-off coupons named the trailers (ADR 0138). Its coupons came out
   * empty, so nothing printed is restored here — the reason is the neighbour's: a reprint of an
   * old sheet already fills the coupon's organisation, make and plate from keys it always had (the
   * template is read from disk), and leaving the trailer row alone empty would print a coupon that
   * contradicts its own header. The row is assembled from that header's boxes by the issue rule.
   */
  if (!('trailers_brands' in data)) {
    const coupon = trailerCouponLines([
      { model: data.trailer1_brand, regNumber: data.trailer1_reg_number },
      { model: data.trailer2_brand, regNumber: data.trailer2_reg_number },
    ]);
    patched.trailers_brands = coupon.brands;
    patched.trailers_reg_numbers = coupon.regNumbers;
  }
  return patched;
}

// ── Журнал учёта (ADR 0037) ──
// Лист — бланк строгой отчётности: журнал отвечает, какие номера выданы, на какие машины и что
// с ними стало. Аннулированные из него не исчезают — пропуск в нумерации означал бы утраченный
// бланк, а не отменённый рейс.

export interface WaybillRequestLinkDto {
  requestId: string;
  /** «ТС-501» — номер заявки, как его читают в портале. */
  displayNumber: string;
  /**
   * Место заявки в задании листа, 1–7. Первые четыре — талоны заказчиков бланка 4-П, остальные
   * печатаются строкой доп. задания и талона не имеют (ADR 0068).
   */
  slot: number;
  objectName: string;
  /**
   * Состояние заявки на сейчас. Журналу оно не колонка — им выбирают, куда вести номер талона:
   * лист выписывают на рейс, а читают журнал позже, когда заявка чаще всего уже закрыта и лежит
   * не в списке заявок, а в журнале закрытых (ADR 0029).
   */
  status: RequestStatus;
}

/**
 * A printed freight task row of the sheet: which trip went on paper as which row
 * (`waybill_trips`, ADR 0108 item 20).
 *
 * Only freight rows within the form's capacity are here. A linear day has no trip, and its row is
 * named by its coupon in `requests`; a relocation prints its task from the route itself. So an
 * empty list is a legitimate answer, not a lost link.
 */
export interface WaybillTripLinkDto {
  tripId: string;
  requestId: string;
  /** «ТС-40/2» — request number and trip number, the way the route card names the row. */
  displayNumber: string;
  /** Task row of the form, 1–10 (ADR 0068): the order of the paper, not of the request. */
  slot: number;
  /**
   * The trip was removed from its request after the sheet was issued (ADR 0108 item 13a). The link
   * stays: a strict-accounting journal must remember what was printed, and the number is never
   * reused, so «ТС-40/2» here still means the trip that went on paper.
   */
  deleted: boolean;
}

/**
 * Under what the sheet's number was spent (`waybills.issue_warnings`, ADR 0108 item 21).
 *
 * Three states, not two, and that is the whole point of the column: `not_checked` (issued before
 * the column existed, or by a path without the handshake — the weekly ESM-2 written by
 * reconciliation), `clean` (checked, nothing to warn about) and `acknowledged` (warnings were shown
 * and a person confirmed them). Six months later «not checked» must not read as «all clean».
 */
export const WAYBILL_ISSUE_CHECK_STATUSES = ['not_checked', 'clean', 'acknowledged'] as const;
export type WaybillIssueCheckStatus = (typeof WAYBILL_ISSUE_CHECK_STATUSES)[number];

export const waybillIssueCheckLabels: Record<WaybillIssueCheckStatus, string> = {
  not_checked: 'При выписке не проверялся',
  clean: 'Проверен при выписке: предупреждений не было',
  acknowledged: 'Выписан с подтверждёнными предупреждениями',
};

/**
 * One confirmed warning as the person read it at issue time.
 *
 * The text is the one stored with the sheet, not re-rendered from today's dictionary: the record
 * answers «what did the dispatcher agree to», and a reworded message must not rewrite that. The
 * code is kept as stored, a plain string, for the same reason. The machine facts behind the
 * fingerprint (person and point ids) are not exposed: nobody reads them, and the journal is open
 * to scoped readers.
 */
export interface WaybillIssueWarningDto {
  code: string;
  message: string;
  /** What the warning is about: «ТС-40/2», «Р-12», the driver's name. */
  entities: string[];
}

export interface WaybillIssueCheckDto {
  status: WaybillIssueCheckStatus;
  /** The confirmed warnings; empty unless `acknowledged`. */
  warnings: WaybillIssueWarningDto[];
}

export interface WaybillDto {
  id: string;
  /** «260604-646-00000004897» — как номер напечатан на бланке. */
  number: string;
  formCode: WaybillFormCode;
  status: WaybillStatus;
  /** День, на который выписан лист; у ЭСМ-2 — первый рабочий день его недели. */
  issuedForDate: string;
  /**
   * Неделя работы машины у ЭСМ-2: первый и последний рабочий день листа. `null` у 4-П и формы
   * № 3 — те выписываются на рейс одного дня, и периода у них нет вовсе.
   */
  periodFrom: string | null;
  periodTo: string | null;
  organizationName: string;
  vehicleId: string;
  /** «КамАЗ 65201 · Е646СК799» — чем именно ехали. */
  vehicleLabel: string;
  driverPersonId: string;
  driverName: string;
  withTrailer: boolean;
  trailerLabel: string;
  issuedByName: string;
  issuedAt: string;
  cancelledByName: string | null;
  cancelledAt: string | null;
  cancelReason: string;
  /**
   * Когда лист печатали и когда выгружали файлом — последний раз и кем угодно (`null` — ни разу).
   *
   * Это отметка о факте, а не о намерении: печать пачкой считается наравне с одиночной, и чужая
   * печать наравне со своей. Вопрос, на который она отвечает, один — «эта бумага уже уехала?»:
   * второй экземпляр того же номера на руках у двух людей хуже, чем лишний поход в журнал.
   *
   * Считается по журналу аудита, где эти события и так пишутся (`waybill.print`,
   * `waybill.export`), — своих колонок у листа под это нет.
   */
  printedAt: string | null;
  exportedAt: string | null;
  /**
   * Лист прошёл через операцию коррекции (ADR 0101 п. 20) — родился ею или был ею списан — **либо**
   * его период сокращали (Р12 плана `docs/vehicle-request-actual-end-date-plan.md`).
   *
   * Источников поэтому три, и первые два считаются по ссылке на операцию (`correction_id`,
   * `cancel_correction_id`), а не по `corrects_waybill_id`: заменяемого листа нет ни у списания без
   * перевыписки (Р20), ни у выписки задним числом по существующему рейсу и у недели ЭСМ-2 (дыры 1
   * и 3), — а в фильтр бухгалтерии они попасть обязаны, потому что это ровно те правки прошедшего
   * дня, ради следа которых фича и заведена.
   *
   * Третий — `period_trimmed_at`: лист, у которого закрытие заявки фактической датой отняло дни с
   * конца. Своей метки у сокращения нет намеренно (решение заказчика по В3): бухгалтерии важно не
   * то, каким механизмом бланк привели к нынешнему виду, а то, что он **не такой, каким выдан**, —
   * и отдельная метка рядом с «коррекцией» заставляла бы держать в голове разницу, которой у
   * читателя журнала нет.
   *
   * Цена решения записана вслух (Р12): отбор «только коррекции» перестал означать «что делали
   * задним числом» и означает «какие бланки не такие, какими выданы». Сокращение бывает и
   * сегодняшним днём, и такой лист в отбор попадёт. Разделить признаки потом можно без миграции —
   * `trimmedAt` в ответе уже лежит отдельным полем.
   */
  isCorrection: boolean;
  /**
   * Почему этот номер такой. У листа, рождённого коррекцией, — причина операции
   * (`correction_reason`); у списанного ею причина лежит в `cancelReason` вместе со всеми прочими
   * аннулированиями, и второй колонки под ту же фразу не заводится.
   */
  correctionReason: string;
  /**
   * Номер заменённого листа — «заменил №…». Хронология журнала при коррекции рвётся неизбежно:
   * номер берётся из хвоста серии (Р10), и за один день в журнале стоят два. Ссылка — единственное,
   * чем разрыв объясняется.
   */
  correctsNumber: string | null;
  /** Номер листа, выписанного взамен этого, — «заменён №…»; обратная сторона той же связи. */
  correctedByNumber: string | null;
  /**
   * Каким был последний день периода **при выписке** бланка; `null` — период не сокращали ни разу
   * (Р12).
   *
   * Пара к `periodTo`, а не замена ему: `periodTo` отвечает «до какого числа лист действует
   * сейчас», это поле — «что напечатано в графе „Период работы“ на экземпляре, который лежит на
   * площадке». Расхождение намеренное (Р13), и без обоих чисел его не показать: одно только
   * «сокращён» не говорит, насколько именно, а второе сокращение того же листа сюда не пишется —
   * колонка помнит выписку, а не предыдущую правку.
   */
  periodToOriginal: string | null;
  /**
   * Когда лист сократили в последний раз; `null` — не сокращали. Им же считается третий источник
   * `isCorrection`: признак «бланк не такой, каким выдан» — это ровно «правка была».
   */
  trimmedAt: string | null;
  /**
   * Почему период сократили — причина **последней** правки (`period_trim_reason`), пустая строка у
   * несокращённого листа.
   *
   * Отдельно от `correctionReason` и `cancelReason`, хотя показываются они в одном месте: те две
   * объясняют операцию с номером (родился коррекцией, списан ею), а эта — почему у действующего
   * бланка отняли дни. Слить их в одно поле значило бы отдать порталу фразу, не сказав, к какому
   * из трёх событий она относится.
   */
  trimReason: string;
  /**
   * Рейс, по которому лист выписан, — «Р-12». Журналу он нужен затем же, зачем талоны заказчиков:
   * бумагу читают позже события, и вопрос «а что это была за поездка» задаётся из журнала, а не
   * из списка заявок.
   *
   * `null` бывает у двух видов строк, и обе законные. У листа ЭСМ-2 рейса нет вовсе: неделя
   * работы машины на площадке рейсом не описывается (миграция 0087). И у листов, выданных до
   * появления маршрутов: пока история не перенесена, они живут в журнале наравне с новыми — и
   * показываются так же, только без номера рейса.
   */
  routeId: string | null;
  routeNumber: string | null;
  /** Заявки, которые машина выполняет по этому листу — талоны заказчиков. */
  requests: WaybillRequestLinkDto[];
  /**
   * Printed freight task rows by trip, in form order (ADR 0108 item 20). A coupon answers «whose
   * work is on this sheet», this list answers «which trip is which row»: a request with three
   * trips has one coupon and three rows.
   */
  trips: WaybillTripLinkDto[];
  /** Under which warnings the number was spent (ADR 0108 item 21). */
  issueWarnings: WaybillIssueCheckDto;
  /**
   * Что подшито к бланку: скан заполненного заказчиком оборота, фотография отметок, акт. Портал
   * этих значений не разбирает — журнал учёта отвечает, чем кончился выданный номер.
   */
  files: FileDto[];
}

/**
 * Лист, выписанный по заявке (ADR 0041): его показывает карточка заявки, чтобы печатать бланк
 * там же, где заявку взяли в работу, а не искать её номер в журнале.
 *
 * У грузоперевозки он один — рейс один. У заказа техники на объект их столько, сколько недель в
 * сроке: карточка показывает список, а `periodFrom`/`periodTo` отвечают, какая это неделя.
 */
export interface RequestWaybillDto {
  id: string;
  /** «260604-646-00000004897» — как номер напечатан на бланке. */
  number: string;
  formCode: WaybillFormCode;
  status: WaybillStatus;
  issuedForDate: string;
  /** Неделя работы у ЭСМ-2; `null` у листов на рейс (см. `WaybillDto`). */
  periodFrom: string | null;
  periodTo: string | null;
  /** Талон заказчика, в котором стоит эта заявка: 1–4 (ADR 0037 п. 3). */
  slot: number;
  driverName: string;
  /**
   * Рейс, по которому лист выписан (маршруты). `null` — лист выдан до маршрутов и ещё не
   * перенесён: пока история не перенесена, такие листы живут наравне с новыми, и карточка
   * показывает их так же — без номера рейса.
   */
  routeId: string | null;
  routeNumber: string | null;
}

export const WAYBILL_SORT_FIELDS = ['issuedForDate', 'number', 'issuedAt'] as const;

export const waybillListQuerySchema = baseListQuery(WAYBILL_SORT_FIELDS).extend({
  /** Issue-date window: the journal is read day by day, not across the whole history at once. */
  dateFrom: dateOnlySchema.optional(),
  dateTo: dateOnlySchema.optional(),
  vehicleId: uuidSchema.optional(),
  driverPersonId: uuidSchema.optional(),
  status: waybillStatusSchema.optional(),
  /**
   * Blank form: 4-P, form No. 3 or ESM-2. All three share one strict-accounting journal but are
   * read by different people for different reasons; without this narrowing the weekly
   * special-equipment sheets drown among the daily trip sheets.
   */
  formCode: waybillFormCodeSchema.optional(),
  /**
   * Construction site of the orders the sheet serves. A waybill has no customer column of its
   * own: the site is derived from its orders — the customer coupons (`waybill_requests`) and, for
   * a weekly ESM-2, the source order — exactly the way the journal scope is derived (ADR 0192).
   *
   * A mixed sheet (coupons of two sites) matches both sites: the trip and the paper are one, and
   * the "my site's paper" question has to find it from either side, same as the scope does (P2).
   * Department-customer orders carry no site, so their sheets never match this filter.
   */
  objectId: uuidSchema.optional(),
  /**
   * Corrections filter (P28, ADR 0101 item 20): accounting reads what was changed retroactively
   * and exports that selection; it also explains a numbering gap on a day that holds two numbers.
   *
   * Two-sided on purpose: `false` answers "what went the ordinary way", which is asked just as
   * often, and a lone "corrections only" flag cannot express it.
   *
   * Must be computed from the same three sources as `isCorrection`, period trimming included
   * (P12), and **both branches** must be edited together: drop the third condition from the
   * `false` branch and a trimmed sheet lands in "ordinary" — the filter would lie exactly where it
   * exists for reconciliation, and nothing would fail to signal it.
   */
  correction: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === 'true')),
});
export type WaybillListQuery = z.infer<typeof waybillListQuerySchema>;

/**
 * Аннулирование листа. Причина обязательна: испорченный бланк списывают, и в журнале должно быть
 * видно, почему номер не ушёл в рейс.
 */
export const cancelWaybillSchema = z
  .object({
    reason: z.string().trim().min(1, 'Укажите причину').max(2000),
    /**
     * Ключ идемпотентности операции (Р31) — нужен списанию задним числом (Р20): оно заводит запись
     * коррекции, и повтор после обрыва связи обязан вернуть прежний результат, а не второй раз
     * объявить об аннулировании того же бланка.
     *
     * Необязателен, потому что сегодняшнее списание операцией не является: испорченный при печати
     * бланк — обычная дневная работа, и требовать под неё ключ значило бы объявить коррекцией всё
     * подряд. Сервер спрашивает ключ ровно тогда, когда дата листа уже прошла.
     */
    operationId: uuidSchema.optional(),
  })
  .strict();
export type CancelWaybillInput = z.infer<typeof cancelWaybillSchema>;

/**
 * Можно ли ещё аннулировать лист (ADR 0037 п. 9 в редакции ADR 0052). По последний день листа
 * включительно — да; со следующего дня — нет: работа состоялась, бланк отработал, и лист стал
 * историей, а не планом.
 *
 * Последний день у листов разный, и в этом всё различие. У 4-П и формы № 3 это день рейса:
 * граница «строго до даты выезда» писалась, когда лист рождался переводом заявки в работу — часто
 * накануне, — а с маршрутами документ выписывают утром дня выезда, и «нельзя в день выезда»
 * означало бы «нельзя никогда». У ЭСМ-2 это последний рабочий день недели: неделя ещё идёт, бланк
 * ещё у машиниста, и досрочно завершённая заявка обязана успеть его переписать.
 */
export function canCancelWaybill(
  waybill: { issuedForDate: string; periodTo?: string | null },
  today: string,
): boolean {
  return today <= (waybill.periodTo || waybill.issuedForDate);
}

export const WAYBILL_LOCKED_MESSAGE =
  'Лист выписан на прошедший день — работа состоялась, и аннулировать бланк уже нельзя';

// ── Коррекция задним числом (ADR 0101) ──
//
// До ADR 0101 прошедший день был закрыт наглухо: `canCancelWaybill` не пускала ни одну роль, и
// бэклог ADR 0052 объяснял это словами «переписывать историю бланком строгой отчётности нельзя».
// Постановка изменилась не в этом: переписывать историю по-прежнему нельзя — лист не правится
// (Р1), и коррекция это «аннулировать и выписать заново». Изменилось то, что **документ обязан
// сойтись с тем, что было**: на рейс выехала другая машина с другим водителем, и бланк, в котором
// напечатана не та техника, — не история, а ошибка. Поэтому прошлое открывается, но под своим
// правом, с обязательной причиной и с записью операции (Р16).
//
// Здесь живёт единый предикат Р29 и то, что от него зависит. Все входы — заведение рейса, выписка
// листа, заведение и правка заявки, сверка ЭСМ-2, аннулирование, коррекция, перенос — спрашивают
// его, а не считают границу каждый по-своему: разошедшись на день, две проверки дали бы отказ там,
// где соседняя ручка тем же телом отвечает согласием.

export { WAYBILL_CORRECTION_DAYS } from './time';
export type { BackdateAccess } from './time';

/**
 * Почему отказано. Код машинный: по нему маршрут выбирает статус ответа (`permission` → 403,
 * `reason` и `limit` → 422), а не по тексту. Различать обязательно — «нет права», «не хватает
 * причины» и «слишком давно» это три разных поручения человеку, и один код на всех сделал бы
 * отказ нечитаемым (Р29).
 */
export type BackdateDenial = 'permission' | 'reason' | 'limit';

/**
 * Вердикт по дате операции. `backdated` в успехе — не украшение: им вызывающий решает, заводить ли
 * запись операции (Р16) и писать ли причину в лист (Р35). Сегодняшняя выписка ничего этого не
 * порождает и коррекцией не считается — иначе фильтр журнала (Р28) наполнился бы обычной работой.
 */
export type BackdateVerdict =
  { ok: true; backdated: boolean } | { ok: false; code: BackdateDenial; reason: string };

export const BACKDATE_PERMISSION_MESSAGE =
  'Дата в прошлом: переписывать прошедший день можно только с правом на коррекцию задним числом — обратитесь к диспетчеру или администратору';

export const BACKDATE_LIMIT_MESSAGE = `Прошло больше ${WAYBILL_CORRECTION_DAYS} дней — такую давность правит администратор, попросите его выполнить коррекцию`;

export const BACKDATE_REASON_MESSAGE =
  'Укажите причину: задним числом операция проходит только с объяснением — оно остаётся в журнале и в листе';

/**
 * Нижняя граница коррекции — день, глубже которого нужен `waybills.correctBeyondLimit` (Р9, Р37).
 *
 * Отдельной функцией, потому что её спрашивают трое: сам `backdateGuard`, `canCorrectWaybill` и
 * `canCorrectRoute`. Посчитанная в каждом месте заново, она разошлась бы при первой же правке
 * числа — и кнопка портала предлагала бы то, чем ручка отвечает отказом.
 */
export function correctionFloorDateKey(today: string): string {
  return shiftDateKey(today, -WAYBILL_CORRECTION_DAYS);
}

/**
 * Единое правило заднего числа — один предикат на все входы (Р29).
 *
 * `effectiveDate` берётся не «по смыслу», а по таблице §4 плана: у рейса это `routeDate`, у
 * грузовой заявки — день `scheduledAt` по МСК, у листа ЭСМ-2 — `periodTo` (тем же концом недели,
 * которым считает `canCancelWaybill`), у правки срока — та граница, которую двигают. Ошибка здесь
 * дороже всех прочих: сдвинув эффективную дату на день, сдвигаешь границу целиком.
 *
 * Порядок проверок выбран так, чтобы первым читалось то, что **здесь и сейчас не исправить**:
 *
 * 1. право — без него не поможет ни причина, ни давность: человеку нужен другой человек;
 * 2. глубина — её не снять ничем, что можно дописать в форму, и узнать о ней после того, как
 *    объяснение уже написано, значит потратить работу впустую;
 * 3. причина — единственное, что проситель добавляет сам, поэтому спрашивается последней.
 *
 * Обратный порядок («сначала причина») выглядел бы дружелюбнее в форме, где причина всё равно
 * обязательна, но в API давал бы два круга отказов подряд на одном и том же запросе.
 */
export function backdateGuard(input: {
  /** Эффективная дата операции — по таблице §4 плана; ошибиться в ней значит сдвинуть границу. */
  effectiveDate: string;
  /** Сегодня по МСК (`moscowDateKeyOf`): границы считаются в одном поясе на обеих сторонах. */
  today: string;
  /** `waybills.correct` у субъекта. */
  allowed: boolean;
  /** `waybills.correctBeyondLimit`: снимает предел дней, но не требование права и причины. */
  unlimited: boolean;
  /** Причина передана и непуста — сам текст предикату не нужен, он его не разбирает. */
  hasReason: boolean;
}): BackdateVerdict {
  // Сегодня и вперёд — обычная работа: ни права, ни причины, ни записи операции. Граница
  // включительная, как у `canCancelWaybill`: лист выписывают утром того же дня, и «сегодня уже
  // прошлое» означало бы, что задним числом делается вся дневная работа диспетчера.
  if (input.effectiveDate >= input.today) return { ok: true, backdated: false };
  if (!input.allowed) return { ok: false, code: 'permission', reason: BACKDATE_PERMISSION_MESSAGE };
  if (!input.unlimited && input.effectiveDate < correctionFloorDateKey(input.today)) {
    return { ok: false, code: 'limit', reason: BACKDATE_LIMIT_MESSAGE };
  }
  if (!input.hasReason) return { ok: false, code: 'reason', reason: BACKDATE_REASON_MESSAGE };
  return { ok: true, backdated: true };
}

/**
 * Подлежит ли лист коррекции: граница глубины плюс состояние номера.
 *
 * Аннулированный не подлежит — и это не формальность (Р32). Аннулированный номер уже списан, и
 * заменять в нём нечего: если взамен него выписан следующий, корректируют этот следующий, а
 * цепочка `A → B → C` читается журналом по звеньям. Разрешив «скорректировать аннулированный»,
 * портал получил бы два разных листа, заменяющих один номер, — и уникальный индекс
 * `waybills_corrects_unique` отказал бы уже в транзакции, после сгоревшей работы.
 *
 * Право и причину проверяет `backdateGuard`: здесь только то, что от субъекта не зависит. Глубина
 * зависит — но ровно одним флагом, и он приходит параметром, чтобы граница считалась в одном
 * месте (`correctionFloorDateKey`), а не двумя разными способами у портала и у сервера.
 */
export function canCorrectWaybill(
  waybill: {
    issuedForDate: string;
    /** Конец недели у ЭСМ-2; у листа на рейс его нет — там границу задаёт день выезда. */
    periodTo?: string | null;
    status: WaybillStatus;
  },
  today: string,
  options: { unlimited?: boolean } = {},
): boolean {
  if (waybill.status !== 'issued') return false;
  if (options.unlimited === true) return true;
  return (waybill.periodTo || waybill.issuedForDate) >= correctionFloorDateKey(today);
}

/**
 * Отказ по самому листу — то, что не чинится ни правом, ни причиной. Причин у него две, и текст
 * называет обе: третьей у `canCorrectWaybill` нет, а два сообщения на одну кнопку заставляли бы
 * портал угадывать, какое из них показать.
 */
export const WAYBILL_CORRECTION_LOCKED_MESSAGE = `Этот номер коррекции не подлежит: аннулированный лист заменять нечем — исправляйте тот, что выписан взамен него, — а бланк старше ${WAYBILL_CORRECTION_DAYS} дней правит администратор`;

/**
 * Что человек подтверждает в окне коррекции — до нажатия, а не после (Р18, Р10, Р34).
 *
 * Перечисляет необратимое: номер сгорает, новый уходит из хвоста серии (вставить его в середину
 * журнала нельзя), а подшитые к старому листу сканы на новый не переезжают — скан оборота подшит к
 * номеру (ADR 0037 п. 11), и перенос сделал бы его неотличимым от бумаги, пришедшей по новому
 * бланку. Остальное — сколько заявок сменят машину, какие подписи смен снимутся, уходила ли уже
 * бумага — считает сервер и показывает тем же окном: перечислить это константой нечем.
 */
export const WAYBILL_CORRECTION_CONFIRM =
  'Действующий номер будет аннулирован, а лист выписан заново — следующим по серии, поэтому в журнале за этот день останутся оба. Подшитые к старому листу файлы на новый не переедут, а выданный на руки бланк придётся забрать: печатать аннулированный лист портал не даёт.';

/**
 * Аннулированный лист не печатают и не выгружают — ни одной роли.
 *
 * Прежде бумага у аннулированного бланка была доступна наравне с выданным: испорченный бланк
 * подшивают к журналу. Но напечатанный аннулированный лист неотличим от действующего — те же
 * реквизиты, тот же номер, — и оказавшись у водителя, он ездит документом, которого уже нет. Что
 * с номером стало, отвечает сама строка журнала, а заполненный оборот подшивают вложением
 * (ADR 0037 п. 11), не перепечатывая бланк.
 *
 * Правило одно на портал и на сервер: кнопка не должна обещать того, чем ручка ответит отказом.
 */
export function canPrintWaybill(status: WaybillStatus): boolean {
  return status !== 'cancelled';
}

export const WAYBILL_CANCELLED_PRINT_MESSAGE =
  'Лист аннулирован: номер списан, и печатать или выгружать такой бланк нельзя';

/**
 * Пачка листов, печатаемая одним документом (ADR 0041 в редакции массовой печати).
 *
 * Предел — не про сервер, а про бумагу и терпение: каждый лист это отдельный бланк A4, и полсотни
 * страниц печатают уже пачкой в лотке, а не «ещё разок». Он же держит время сборки: бланки
 * переводит в PDF LibreOffice, и на сотне листов запрос ушёл бы за минуту.
 */
export const WAYBILL_PRINT_BATCH_LIMIT = 50;

export const printWaybillsBatchSchema = z
  .object({
    /**
     * Листы в том порядке, в каком их напечатать. Порядок задаёт портал (как в журнале), а не
     * сервер: пачку разбирают по столу в том же виде, в каком её видели на экране.
     */
    ids: z.array(uuidSchema).min(1, 'Выберите хотя бы один лист').max(WAYBILL_PRINT_BATCH_LIMIT),
  })
  .strict();
export type PrintWaybillsBatchInput = z.infer<typeof printWaybillsBatchSchema>;

/** Сколько листов выбрано — так это подписывает виджет над пагинацией: «Выбрано 3 листа». */
export function selectedWaybillsLabel(count: number): string {
  const mod100 = count % 100;
  const mod10 = count % 10;
  const word =
    mod100 >= 11 && mod100 <= 14
      ? 'листов'
      : mod10 === 1
        ? 'лист'
        : mod10 >= 2 && mod10 <= 4
          ? 'листа'
          : 'листов';
  return `Выбрано ${count} ${word}`;
}

// ── Вложения к листу ──

/**
 * Что подшивают к номеру бланка: скан заполненного заказчиком оборота ЭСМ-2, фотографию отметок
 * 4-П, акт. Файл крепится к самому листу, а не к заявке: заявок у листа бывает четыре, а неделя
 * работ распадается на несколько листов — «чей это скан» отвечает только номер бланка.
 *
 * Лимит держит сервер тем же `config.files.maxPerRequest`, что и у заявок: файловая подсистема
 * одна на портал (ADR 0024).
 */
export const attachWaybillFilesSchema = z
  .object({ addFileIds: z.array(uuidSchema).min(1).max(20) })
  .strict();
export type AttachWaybillFilesInput = z.infer<typeof attachWaybillFilesSchema>;
